import React, { useState, useEffect, useRef } from 'react';
import { createLogger } from './logger';

const logger = createLogger({ module: 'RouteStabilizer' });

// Delay duration before allowing redirect to non-authenticated routes
const STABILITY_DURATION = 50; // Reduced from 100ms to 50ms for even faster transitions

interface RouteStabilizerProps {
  // The component to render if authenticated
  authenticated: React.ReactNode;
  // The component to render if not authenticated
  unauthenticated: React.ReactNode;
  // Whether user is currently authenticated
  isAuthenticated: boolean;
  // Whether this is a hard redirect (full page reload) or React Router redirect
  useHardRedirect?: boolean;
  // Path to navigate to for hard redirects
  redirectPath?: string;
}

/**
 * A component that stabilizes route changes during authentication state transitions
 * to prevent flickering between authenticated and unauthenticated states
 */
export const RouteStabilizer: React.FC<RouteStabilizerProps> = ({
  authenticated,
  unauthenticated,
  isAuthenticated,
  useHardRedirect = false,
  redirectPath = '/home'
}) => {
  // Track the stable auth state
  const [stableAuth, setStableAuth] = useState(isAuthenticated);
  // Track if we've ever been authenticated in this component lifecycle
  const everAuthenticatedRef = useRef(false);
  // Track the timeout for stability
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track if a redirect has already been attempted
  const redirectAttemptedRef = useRef(false);
  // Track errors during rendering
  const [renderError, setRenderError] = useState<Error | null>(null);

  // Update stable auth state with debouncing
  useEffect(() => {
    try {
      // If becoming authenticated, update immediately
      if (isAuthenticated) {
        everAuthenticatedRef.current = true;
        
        // Clear any pending timeout that might revert to unauthenticated
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        
        setStableAuth(true);
        logger.debug('Setting stable auth state to authenticated');
      } 
      // If losing authentication and we were previously authenticated,
      // wait a bit before updating to prevent flickering
      else if (everAuthenticatedRef.current && stableAuth) {
        logger.debug('Detected potential auth state loss, stabilizing for period', {
          duration: STABILITY_DURATION
        });
        
        // Set a timeout to update the state
        timeoutRef.current = setTimeout(() => {
          logger.debug('Stability period ended, updating auth state to unauthenticated');
          setStableAuth(false);
          timeoutRef.current = null;
        }, STABILITY_DURATION);
      } 
      // Otherwise update immediately
      else {
        setStableAuth(isAuthenticated);
      }
    } catch (error) {
      logger.error('Error updating stable auth state:', error);
      // Fall back to direct state update on error
      setStableAuth(isAuthenticated);
    }
    
    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isAuthenticated, stableAuth]);

  // Hard redirect if needed
  useEffect(() => {
    if (useHardRedirect && stableAuth && redirectPath && !redirectAttemptedRef.current) {
      try {
        logger.debug('Performing hard redirect to', { 
          path: redirectPath, 
          stableAuth,
          isAuthenticated,
          everAuthenticated: everAuthenticatedRef.current 
        });
        
        // Mark that we've attempted a redirect to prevent multiple redirects
        redirectAttemptedRef.current = true;
        
        // Add cache-busting query param
        const redirectUrl = `${redirectPath}?t=${Date.now()}`;
        
        // Use setTimeout to ensure the redirect happens after rendering
        setTimeout(() => {
          try {
            window.location.href = redirectUrl;
          } catch (error) {
            logger.error('Error performing hard redirect:', error);
            // In case of error, reset the redirect attempt flag
            redirectAttemptedRef.current = false;
          }
        }, 50);
      } catch (error) {
        logger.error('Error setting up redirect:', error);
        setRenderError(error instanceof Error ? error : new Error(String(error)));
      }
    }
  }, [useHardRedirect, stableAuth, redirectPath, isAuthenticated]);

  // If there was an error, show an error UI
  if (renderError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <h1 className="text-2xl font-bold mb-4 text-red-600">Something went wrong</h1>
          <p className="mb-4 text-gray-700">We encountered an error while loading the page.</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  // Return appropriate component based on stable auth state
  return (
    <>{stableAuth ? authenticated : unauthenticated}</>
  );
}; 